import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Linkedin, Mail, ArrowUp, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { IDENTITY } from "@/content";

const EASE = [0.22, 1, 0.36, 1];

const InquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", budget: "Not sure yet", details: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Inquiry sent — Aditi will get back to you soon.");
      setForm({ name: "", email: "", budget: "Not sure yet", details: "" });
      onClose();
    } catch {
      toast.error(`Couldn't send your inquiry. Please email ${IDENTITY.email} directly.`);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-border/70 bg-background/60 px-4 py-3 text-sm outline-none transition-colors focus:border-terra";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-background/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="contact-modal-overlay"
        >
          <motion.form
            onSubmit={submit}
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-t-[2rem] border border-border/60 bg-card p-8 shadow-2xl sm:rounded-[2rem] sm:p-10"
            data-testid="contact-form"
          >
            <div className="mb-7 flex items-start justify-between">
              <div>
                <p className="mono-label mb-2">Quick inquiry</p>
                <h3 className="font-serif text-2xl tracking-tight">Start a project</h3>
              </div>
              <button
                type="button"
                data-testid="contact-modal-close"
                onClick={onClose}
                aria-label="Close inquiry form"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:text-terra"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <input
                data-testid="contact-form-name"
                required
                placeholder="Your name"
                value={form.name}
                onChange={set("name")}
                className={inputCls}
              />
              <input
                data-testid="contact-form-email"
                required
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
              />
              <select
                data-testid="contact-form-budget"
                value={form.budget}
                onChange={set("budget")}
                className={inputCls}
              >
                <option>Not sure yet</option>
                <option>Under ₹25k</option>
                <option>₹25k — ₹75k</option>
                <option>₹75k+</option>
              </select>
              <textarea
                data-testid="contact-form-details"
                required
                rows={4}
                placeholder="Tell me about your project…"
                value={form.details}
                onChange={set("details")}
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={sending}
              className="mt-7 w-full rounded-full bg-terra py-3.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send inquiry"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Contact = () => {
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: IDENTITY.timezone,
      hour: "2-digit",
      minute: "2-digit",
    });
    const tick = () => setTime(fmt.format(new Date()));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const copyEmail = async () => {
    await navigator.clipboard.writeText(IDENTITY.email);
    setCopied(true);
    toast.success("Email copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const linkedInClick = (e) => {
    if (!IDENTITY.linkedin) {
      e.preventDefault();
      toast.info("LinkedIn link coming soon — Aditi will add her profile URL here.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative scroll-mt-24 overflow-hidden px-6 pt-24 pb-10 sm:px-12 lg:px-20 lg:pt-32"
    >
      <div className="pointer-events-none absolute bottom-[-30%] left-1/2 h-[30rem] w-[46rem] -translate-x-1/2 rounded-full bg-terra/15 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl">
        <Reveal>
          <p className="mono-label mb-6">04 — Contact</p>
          <h2
            data-testid="contact-heading"
            className="max-w-4xl font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            Ready to <span className="italic text-terra">scale</span> your brand?
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <button
              data-testid="contact-email-button"
              onClick={copyEmail}
              className="group flex items-center gap-3 rounded-full border border-border/70 bg-card/70 px-6 py-3.5 transition-colors duration-300 hover:border-terra/60"
            >
              <Mail size={16} className="text-terra" />
              <span className="text-sm font-medium">{IDENTITY.email}</span>
              {copied ? <Check size={15} className="text-sage" /> : <Copy size={15} className="text-muted-foreground transition-colors group-hover:text-terra" />}
            </button>

            <a
              data-testid="contact-linkedin-link"
              href={IDENTITY.linkedin || "#"}
              onClick={linkedInClick}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-full border border-border/70 bg-card/70 px-6 py-3.5 text-sm font-medium transition-colors duration-300 hover:border-terra/60"
            >
              <Linkedin size={16} className="text-terra" />
              LinkedIn
            </a>

            <button
              data-testid="contact-start-project-button"
              onClick={() => setModalOpen(true)}
              className="rounded-full bg-terra px-7 py-3.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground transition-transform duration-300 hover:scale-[1.04]"
            >
              Start a project
            </button>
          </div>
        </Reveal>

        <footer className="mt-24 flex flex-wrap items-center justify-between gap-4 border-t border-border/50 pt-8 pb-4" data-testid="footer">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            © 2026 {IDENTITY.name} · Pune, IN · <span data-testid="footer-clock">{time} IST</span>
          </p>
          <a
            data-testid="back-to-top-button"
            href="#top"
            aria-label="Back to top"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors duration-300 hover:border-terra hover:text-terra"
          >
            <ArrowUp size={16} />
          </a>
        </footer>
      </div>

      <InquiryModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
};
